import { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { enqueueSnackbar } from 'notistack';
import { boardToBoardInfo } from '@gorilla/widgets-shared/src/services/publish';
import { buildWidgetPath } from '@gorilla/widgets-shared/src/utils';
import { listWidgets, createWidget, deleteWidget, getWidgetsInfo, WidgetSummary } from '../services/widgets';
import { getSettings, saveSettings } from '../services/settings';
import { getDefaultSettings } from '../services/default-settings';
import { useMondayContext } from './use-monday-context';
import { useBoard } from './use-board';

export type Widget = WidgetSummary & { publishedAt?: number };

export function useWidgets() {
  const { data: mondayContext, error: mondayContextError } = useMondayContext();
  const { data: board } = useBoard(mondayContext?.boardId);
  const boardId = mondayContext?.boardId;

  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const {
    data: widgets,
    error: widgetsError,
    mutate: refetchWidgets,
  } = useSWR(
    boardId ? `/widgets/${boardId}` : null,
    async () => {
      const rows = await listWidgets(boardId!);
      const info = await getWidgetsInfo(rows.map((row) => row.path));
      const publishedAtByPath = new Map(info.map((entry) => [entry.path, entry.publishedAt]));

      return rows.map((row): Widget => ({ ...row, publishedAt: publishedAtByPath.get(row.path) }));
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  // a board that has never been configured has no row yet - one is created by the first settings
  // save. the editor works off the path alone, so the board's default widget is synthesised here
  // and the user lands straight in it, exactly as they did before a board could hold several.
  const pendingDefaultWidget: Widget | null =
    boardId && widgets && widgets.length === 0
      ? { id: `pending:${boardId}`, name: null, path: buildWidgetPath(boardId), isDefault: true, createdAt: '' }
      : null;

  // one widget - the board's own or the pending default - opens straight away, so users who only
  // ever have one land in the editor as they always have. with more than one the list is the
  // starting point, since it is the only place that shows them all.
  // this runs once per mount: re-running it would immediately re-open the widget a user just left,
  // making the list unreachable on a single-widget board.
  const didAutoSelect = useRef(false);

  useEffect(() => {
    if (!widgets || !boardId || didAutoSelect.current) {
      return;
    }

    didAutoSelect.current = true;

    if (widgets.length === 1) {
      setSelectedWidgetId(widgets[0].id);
    } else if (pendingDefaultWidget) {
      setSelectedWidgetId(pendingDefaultWidget.id);
    }
  }, [widgets, boardId]);

  function selectWidget(widgetId: string | null) {
    setSelectedWidgetId(widgetId);

    // returning to the list: the widget may have been created by its first save, renamed, or
    // published there, none of which the cached list knows about
    if (widgetId === null) {
      refetchWidgets();
    }
  }

  async function withBusy<T>(action: () => Promise<T>, errorMessage: string) {
    setIsBusy(true);

    try {
      return await action();
    } catch (err) {
      enqueueSnackbar((err as Error)?.message || errorMessage, { variant: 'error' });
      return null;
    } finally {
      setIsBusy(false);
    }
  }

  async function addWidget(name: string) {
    if (!boardId || !board) {
      return null;
    }

    const widget = await withBusy(
      () => createWidget({ boardId, name, data: getDefaultSettings(boardToBoardInfo(board)) }),
      'Failed to create widget',
    );

    // creating leaves the user on the list, the same as duplicating does - the new row appearing is
    // the confirmation, and a user adding a widget to a board that already has several would
    // otherwise lose the list they were working in
    if (widget) {
      await refetchWidgets();
    }

    return widget;
  }

  async function duplicateWidget(source: Widget) {
    if (!boardId) {
      return null;
    }

    const widget = await withBusy(
      () => createWidget({ boardId, name: `${source.name || 'Widget'} (copy)`, copyFromId: source.id }),
      'Failed to duplicate widget',
    );

    if (widget) {
      await refetchWidgets();
    }

    return widget;
  }

  // the name lives inside the settings blob, so renaming means a read-modify-write of that widget's
  // settings rather than a dedicated endpoint
  async function renameWidget(widget: Widget, name: string) {
    const result = await withBusy(async () => {
      if (!board) {
        throw new Error('Board not loaded yet');
      }

      const data = await getSettings(widget.path);

      if (!data) {
        throw new Error('Widget not found');
      }

      // the editor merges the defaults into what it loads before it ever saves. renaming writes the
      // whole blob back too, so it has to do the same - a row that predates a setting the schema now
      // requires would otherwise be rejected by the validator.
      const saved = await saveSettings(widget.path, { ...getDefaultSettings(boardToBoardInfo(board)), ...data, name });

      // saveSettings answers with null on a 404, so a missing widget has to be caught here as well
      // as through `error` - otherwise the rename passes silently and the list is never refreshed
      if (!saved || saved.error) {
        throw new Error('Failed to rename widget');
      }

      return saved;
    }, 'Failed to rename widget');

    if (result) {
      await refetchWidgets();
    }

    return result;
  }

  async function removeWidget(widgetId: string) {
    const result = await withBusy(() => deleteWidget(widgetId), 'Failed to delete widget');

    if (result) {
      if (selectedWidgetId === widgetId) {
        selectWidget(null);
      }

      await refetchWidgets();
    }

    return result;
  }

  const selectedWidget =
    widgets?.find((widget) => widget.id === selectedWidgetId) ||
    (pendingDefaultWidget?.id === selectedWidgetId ? pendingDefaultWidget : null);

  return {
    mondayContext,
    mondayContextError,
    board,
    widgets,
    widgetsError,
    isBusy,
    selectedWidget,
    selectWidget,
    addWidget,
    duplicateWidget,
    renameWidget,
    removeWidget,
    refetchWidgets,
  };
}
